import React from 'react';
import type { TaskListItem } from '../../shared/types/task';

interface GestaoKanbanCardProps {
  task: TaskListItem;
  onClick: () => void;
  onOpenExecution?: (executionId: string) => void;
}

function formatTimeRemaining(deadline: string | null | undefined): string {
  if (!deadline) return 'N/D';
  const remaining = new Date(deadline).getTime() - Date.now();
  const hours = Math.floor(Math.abs(remaining) / (1000 * 60 * 60));
  const sign = remaining < 0 ? '-' : '';
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${sign}${days}D`;
  }
  return `${sign}${hours}H`;
}

const priorityConfig: Record<string, { dot: string; label: string }> = {
  critica: { dot: 'bg-red-500', label: 'CRITICA' },
  alta: { dot: 'bg-orange-500', label: 'ALTA' },
  media: { dot: 'bg-blue-500', label: 'MEDIA' },
  baixa: { dot: 'bg-slate-400', label: 'BAIXA' },
};

export const GestaoKanbanCard: React.FC<GestaoKanbanCardProps> = ({ task, onClick, onOpenExecution }) => {
  const slaRemaining = formatTimeRemaining(task.slaDeadline);
  const isOverdue = task.isOverdue;
  const priority = priorityConfig[task.priority ?? 'media'] ?? priorityConfig.media;
  const roleLabel = task.assignedRoleName ?? task.assignedToName ?? task.assignedTo ?? 'NAO_ASSUMIDO';

  return (
    <button
      onClick={onClick}
      className={`w-full cursor-pointer border bg-white px-2 py-1.5 text-left transition-colors dark:bg-[var(--bg-surface)] ${
        isOverdue
          ? 'border-amber-300 dark:border-amber-700'
          : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
          {task.referenceCode ?? task.instanceId.slice(0, 8)}
        </span>
        <span className={`h-2 w-2 rounded-full ${priority.dot}`} title={priority.label} />
      </div>

      <p className="mt-1 truncate text-[11px] font-medium uppercase tracking-[0.12em] text-slate-900 dark:text-slate-100">
        {task.stageName.replace(/\s+/g, '_')}
      </p>

      {task.instanceTitle ? (
        <p className="mt-0.5 truncate text-[11px] text-slate-600 dark:text-slate-300">{task.instanceTitle}</p>
      ) : null}

      <div className="mt-1.5 space-y-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
        <div className="flex items-center justify-between gap-2">
          <span>FUNÇÃO</span>
          <span className="truncate text-slate-700 dark:text-slate-200">{String(roleLabel).replace(/\s+/g, '_').toUpperCase()}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span>PRIO</span>
          <span className="text-slate-700 dark:text-slate-200">{priority.label}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span>SLA_REST</span>
          <span className={isOverdue ? 'text-amber-700 dark:text-amber-300' : 'text-slate-700 dark:text-slate-200'}>
            {slaRemaining}
          </span>
        </div>
      </div>

      {onOpenExecution ? (
        <div className="mt-1.5 border-t border-slate-100 pt-1.5 dark:border-slate-700">
          <span
            role="link"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              onOpenExecution(task.instanceId);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.stopPropagation();
                onOpenExecution(task.instanceId);
              }
            }}
            className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
          >
            ABRIR_EXECUCAO
          </span>
        </div>
      ) : null}
    </button>
  );
};
